import * as React from 'react'

// 1:1 port of WPF SuperTacho.DataModel.TachographChartImport.TachographChartRedoUndo.
// 10-deep stack, dedupes consecutive equal states, redo stack cleared on every push.
// `equals` defaults to JSON-string compare (good enough for plain-object snapshots).

const MAX_STACK_SIZE = 10

export type UndoRedo<T> = {
  current: T
  push: (next: T) => void
  // Overwrites the current state without a new undo step.
  replaceTop: (next: T) => void
  undo: () => void
  redo: () => void
  reset: (initial: T) => void
  canUndo: boolean
  canRedo: boolean
}

export function useUndoRedo<T>(
  initial: T,
  equals: (a: T, b: T) => boolean = defaultEquals,
): UndoRedo<T> {
  // Two stacks + the live "current" pointer. Mirroring WPF: the current state
  // sits at the top of the undo stack; undo pops it onto the redo stack and
  // returns the new top.
  const [undoStack, setUndoStack] = React.useState<T[]>([initial])
  const [redoStack, setRedoStack] = React.useState<T[]>([])

  const current = undoStack[undoStack.length - 1]

  const push = React.useCallback(
    (next: T) => {
      if (equals(next, current)) return
      setUndoStack((s) => {
        const trimmed = s.length >= MAX_STACK_SIZE ? s.slice(s.length - MAX_STACK_SIZE + 1) : s
        return [...trimmed, next]
      })
      setRedoStack([])
    },
    [current, equals],
  )

  const replaceTop = React.useCallback((next: T) => {
    setUndoStack((s) => [...s.slice(0, -1), next])
  }, [])

  const undo = React.useCallback(() => {
    if (undoStack.length <= 1) return
    setUndoStack((s) => s.slice(0, -1))
    setRedoStack((r) => [...r, current])
  }, [undoStack.length, current])

  const redo = React.useCallback(() => {
    setRedoStack((r) => {
      if (r.length === 0) return r
      const next = r[r.length - 1]
      setUndoStack((s) => [...s, next])
      return r.slice(0, -1)
    })
  }, [])

  const reset = React.useCallback((next: T) => {
    setUndoStack([next])
    setRedoStack([])
  }, [])

  return {
    current,
    push,
    replaceTop,
    undo,
    redo,
    reset,
    canUndo: undoStack.length > 1,
    canRedo: redoStack.length > 0,
  }
}

function defaultEquals<T>(a: T, b: T) {
  return JSON.stringify(a) === JSON.stringify(b)
}
